/**
 * The primary backend (PLAN §10.2): whichever endpoint the connectivity
 * manager (`src/connectivity/`) currently names as the data path, and the
 * shared API client pointed at it — with automatic failover
 * (`connectivity/failover.ts`). Built once at start-up; the token is read per
 * request, so signing in and out needs no new client.
 *
 * `kind`, `label`, `apiUrl` and `wsUrl` are read at use: after a failover they
 * name the new primary. Whoever opens a WS (`@acp/api-client/watchClient.js`)
 * reads `wsUrl` when it connects, and re-subscribes when the primary changes
 * (`useConnectivity()`'s `primaryId`).
 */
import type { ApiClient } from '@acp/api-client/client.js';
import type { RuntimeConfigWire } from '@acp/api-contract/runtimeConfig.js';
import type { Endpoint } from '../connectivity/endpoints.js';

export type Backend = {
  readonly kind: RuntimeConfigWire['backend']['kind'];
  readonly label: string;
  readonly apiUrl: string;
  readonly wsUrl: string;
  readonly api: ApiClient;
};

export function createBackend(primary: () => Endpoint, api: ApiClient): Backend {
  return {
    get kind() {
      return primary().kind;
    },
    get label() {
      return primary().label;
    },
    get apiUrl() {
      return primary().apiUrl;
    },
    get wsUrl() {
      return primary().wsUrl;
    },
    api,
  };
}

/** Where a backend's session is persisted: one entry per auth target, so another pool's token is never sent here. */
export function sessionStorageKey(config: RuntimeConfigWire, apiUrl: string): string {
  return config.auth.kind === 'cognito' ? `acp.session.cognito.${config.auth.userPoolId}.${config.auth.clientId}` : `acp.session.dev.${apiUrl}`;
}
